<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="mobilepayattempt-details">
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-money-check"></i> {{ _('MobilePay payment attempt') }}</h3>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="payment-flow" v-if="attempt && attempt.events && attempt.events.length">
            <div class="payment-header">
              <div style="float: right" v-if="attempt.payment && attempt.payment.bill_id" v-on:click="displayInvoice"><button class="btn btn-info btn-xs"><i class="fa fa-fw fa-file"></i> {{ attempt.payment.bill_id }}</button></div>
              <div>{{ _('Reference') }}: <strong>{{ attempt.events[0].reference }}</strong></div>
              <div>{{ _('Amount') }}: <strong>{{ formatAmount(attempt.events[0].amount) }}</strong></div>
            </div>
            
            <div class="payment-timeline">
              <div v-for="event in attempt.events" :key="event.timestamp" class="timeline-event">
                <div class="event-status">
                  <span class="status-badge" :class="event.name.toLowerCase()">{{ event.name }}</span>
                </div>
                <div class="event-details">
                  <div class="event-time">{{ $filters.timestampToDate((new Date(event.timestamp).getTime() / 1000)) }} {{ _('at') }} {{ $filters.timestampToTime((new Date(event.timestamp).getTime() / 1000)) }}</div>
                  <div class="event-psp">PSP Ref: {{ event.pspReference }}</div>
                  <div class="event-amount" v-if="event.name === 'CAPTURED' || event.name === 'REFUNDED'">{{ formatAmount(event.amount) }}</div>
                </div>
              </div>
            </div>
            <template v-if="isRefundable">
              <p><button class="btn btn-primary" v-on:click="refundDialog"><i class="fa fa-fw fa-undo"></i> {{ _('Refund payment attempt') }}</button></p>
              <p><i>{{ _('Only use this refund method if the payment can not be refunded with the transaction, or if the transaction has already been refunded without refunding the payment.') }}</i></p>
            </template>
            <template v-if="isCancellable">
              <p><button class="btn btn-danger" v-on:click="cancelDialog"><i class="fa fa-fw fa-times"></i> {{ _('Cancel payment attempt') }}</button></p>
              <p><i>{{ _('Only use this button if you are certain the transaction is not in progress and you want to cancel it.') }}</i></p>
            </template>
          </div>
          <div v-else-if="loading">
            <div class="alert alert-info">{{ _('Loading...') }}</div>
          </div>
          <div v-else-if="!loading">
            <div class="alert alert-warning">{{ _('Unable to load payment attempt from MobilePay API.') }} {{ _('This typically means that the payment attempt has failed immediately.') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'

export default {
  data () {
    return {
      attempt: {},
      loading: true
    }
  },
  mixins: [components],
  computed: {
    isCancellable () {
      return this.attempt.events && this.attempt.events[this.attempt.events.length - 1].name === 'AUTHORIZED'
    },
    isRefundable () {
      return this.attempt.events && 
             this.attempt.events.some(event => event.name === 'CAPTURED') &&
             this.remainingRefundableAmount > 0
    }, 
    remainingRefundableAmount () {
      const totalCaptured = this.attempt.events.filter(event => event.name === 'CAPTURED').reduce((sum, event) => sum + event.amount.value, 0)
      if (this.attempt.events && this.attempt.events.some(event => event.name === 'REFUNDED')) {
        const totalRefunded = this.attempt.events.filter(event => event.name === 'REFUNDED').reduce((sum, event) => sum + event.amount.value, 0)
        return (totalCaptured - totalRefunded) / 100
      }
      return totalCaptured / 100
    }
  },
  methods: {
    formatAmount(amount) {
      return new Intl.NumberFormat('fi-FI', {
        style: 'currency',
        currency: amount.currency
      }).format(amount.value / 100)
    },
    loadAttempt(reference) {
      return new Promise(async (resolve, reject) => {
        try {
          this.loading = true
          const response = await axios.get(this.baseDomain + this.shopId + '/mobilepayhandler/' + reference + '.json?apikey=' + this.apiKey + '&plugin=app')
          this.attempt = response.data
          resolve(this.attempt)
        } catch (e) {
          reject()
        } finally {
          this.loading = false
        }
      })
    },
    displayInvoice () {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'InvoiceDetails',
        title: this._('Transaction') + ' ' + this.attempt.payment.bill_id,
        width: 6,
        data: {
          invoiceId: this.attempt.payment.bill_id,
          trail: this.componentTrail
        }
      })
    },
    cancelDialog () {
      this.showDialog({
        title: this._('Cancel payment attempt'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.cancel()
            }
          }
        ]
      })
    },
    async cancel () {
      try {
        const data = {
          method: 'Cancel',
          reference: this.attempt.events[0].reference
        }
        await axios.post(this.baseDomain + this.shopId + '/mobilepayhandler.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(this._('Cancel failed!'))
        return
      }
      this.loadAttempt(this.attempt.events[0].reference)
    },
    refundDialog () {
      this.showDialog({
        title: this._('Refund payment attempt'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: (data) => {
              this.refund(data.amount)
            }
          }
        ],
        fields: [
          {
            title: this._('Amount'),
            type: 'text',
            name: 'amount',
            defaultValue: this.remainingRefundableAmount
          }
        ]
      })
    },
    async refund (amount) {
      try {
        const data = {
          mobilepayReference: this.attempt.events[0].reference
        }
        if (amount) {
          data.amount = amount
        }
        await axios.post(this.baseDomain + this.shopId + '/refunds/new.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(this._('Refund failed!'))
        return
      }
      this.loadAttempt(this.attempt.events[0].reference)
    }
  },
  async created () {
    if (this.component && this.component.initData) {
      try {
        await this.loadAttempt(this.component.initData.reference)
      } catch(e) {}
    }
  }
}

</script>

<style scoped>
.payment-flow {
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
}

.payment-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.timeline-event {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: bold;
}

.event-status {
  width: 150px;
}

.status-badge.created {
  background: #e3f2fd;
  color: #1976d2
}

.status-badge.authorized {
  background: #f3e5f5;
  color: #7b1fa2
}

.status-badge.captured {
  background: #e8f5e9;
  color: #388e3c
}

.status-badge.aborted {
  background: #ffebee;
  color: #d32f2f
}

.status-badge.expired {
  background: #fafafa;
  color: #757575
}

.status-badge.cancelled {
  background: #fff3e0;
  color: #ef6c00
}

.status-badge.refunded {
  background: #fffde7;
  color: #fbc02d
}

.status-badge.terminated {
  background: #ffebee;
  color: #c62828
}

.event-details {
  margin-left: 15px;
}

.event-time {
  font-size: 0.9em;
  color: #666;
}

.event-psp {
  font-size: 0.85em;
  color: #888;
}

.event-amount {
  font-size: 0.85em;
  color: #888;
  font-weight: bold;
}
</style>
